/**
 * AnyClip — URL 입력 + 다운로드 폼 컴포넌트
 *
 * 기능:
 * - 클립보드 자동 읽기 (권한 체크 → 자동 붙여넣기)
 * - URL 유효성 검증
 * - 백엔드 API 호출 → 전역 상태 업데이트
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ACTIONS } from '../context/AppContext';
import { useI18n } from '../context/I18nContext';
import { requestDownload } from '../services/api';
import './DownloadForm.css';

// URL 패턴 검증
const URL_PATTERN = /^https?:\/\/(www\.)?(tiktok\.com|instagram\.com|youtube\.com|youtu\.be|twitter\.com|x\.com|facebook\.com|fb\.watch).*/i;
const GENERIC_URL_PATTERN = /^https?:\/\/.+/i;

function DownloadForm() {
  const { state, dispatch } = useApp();
  const { t } = useI18n();
  const [url, setUrl] = useState('');
  const [clipboardBtnVisible, setClipboardBtnVisible] = useState(false);
  const [urlError, setUrlError] = useState('');

  const isLoading = state.status === 'loading';

  // ── 클립보드 자동 읽기 ──────────────────────────────────
  const checkAndPasteClipboard = useCallback(async () => {
    try {
      // 1. 권한 체크
      const permission = await navigator.permissions.query({ name: 'clipboard-read' });

      if (permission.state === 'granted') {
        const text = await navigator.clipboard.readText();
        if (GENERIC_URL_PATTERN.test(text.trim())) {
          setUrl(text.trim());
          setClipboardBtnVisible(false);
        }
      } else if (permission.state === 'prompt') {
        // 권한 요청 필요 → 버튼 표시
        setClipboardBtnVisible(true);
      }
      // denied → 버튼 숨김, 수동 입력만
    } catch {
      // navigator.permissions 미지원 환경 → 버튼 표시
      setClipboardBtnVisible(true);
    }
  }, []);

  // 클립보드 버튼 클릭 시 (권한 프롬프트 포함)
  const handleClipboardPaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (GENERIC_URL_PATTERN.test(text.trim())) {
        setUrl(text.trim());
        setUrlError('');
        setClipboardBtnVisible(false);
      }
    } catch {
      setUrlError(t('error_clipboard'));
    }
  };

  // 마운트 시 자동 클립보드 체크 및 URL 파라미터 확인
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const passedUrl = params.get('url');
    if (passedUrl) {
      setUrl(passedUrl);
      return;
    }
    checkAndPasteClipboard();
  }, [checkAndPasteClipboard]);

  // ── URL 변경 핸들러 ──────────────────────────────────────
  const handleUrlChange = (e) => {
    setUrl(e.target.value);
    setUrlError('');
  };

  const handleClear = () => {
    setUrl('');
    setUrlError('');
  };

  // ── 다운로드 제출 ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedUrl = url.trim();

    // 유효성 검사
    if (!GENERIC_URL_PATTERN.test(trimmedUrl)) {
      setUrlError(t('error_invalid_url'));
      return;
    }

    dispatch({ type: ACTIONS.SET_LOADING });

    // 광고는 ReferralModal "동영상 광고 시청하기" 버튼에서만 실행
    // (다운로드 버튼에서는 광고 없음 — UX 방해 + 티켓 미지급)

    try {

      const tg = window.Telegram?.WebApp;
      const initData = tg?.initData || '';
      const telegramId = tg?.initDataUnsafe?.user?.id || 'dev_user_001';

      const data = await requestDownload(trimmedUrl, initData, telegramId);

      dispatch({
        type: ACTIONS.SET_SUCCESS,
        payload: {
          streamUrl: data.streamUrl,
          type: data.type,
          mediaUrls: data.mediaUrls,
          downloadCount: data.downloadCount,
          tickets: data.tickets,
        },
      });
    } catch (err) {
      const res = err.response;

      // 403 — 레퍼럴 락
      if (res?.status === 403 && res?.data?.error === 'REFERRAL_REQUIRED') {
        dispatch({
          type: ACTIONS.SET_REFERRAL_LOCKED,
          payload: {
            referralLink: res.data.referralLink,
            downloadCount: res.data.downloadCount,
            tickets: res.data.tickets || 0,
          },
        });
        return;
      }

      // 429 — Rate Limit
      if (res?.status === 429) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: { message: t('error_rate_limit') },
        });
        return;
      }

      // 503 — 모든 API 실패
      if (res?.status === 503) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: { message: t('error_maintenance') },
        });
        return;
      }

      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: { message: res?.data?.message || t('error_unknown') },
      });
    }
  };

  return (
    <section className="download-form-section" aria-label="영상 다운로드">
      {/* 다운로드 현황 / 티켓 표시 및 충전 버튼 */}
      {state.status !== 'loading' && (
        <div className="count-badge-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <div className="count-badge" aria-label={`다운로드 현황`} style={{ margin: 0 }}>
            {t('tickets_left')}{state.tickets}
          </div>
          
          {/* 유저가 원할 때 언제든 티켓 충전 모달을 열 수 있는 버튼 */}
          <button
            type="button"
            className="recharge-badge-btn"
            onClick={() => dispatch({ type: ACTIONS.SET_REFERRAL_LOCKED })}
            style={{
              background: 'linear-gradient(135deg, #00C853, #B2FF59)',
              color: '#0a0a0f',
              border: 'none',
              borderRadius: '20px',
              padding: '6px 14px',
              fontSize: '0.85rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 200, 83, 0.3)',
              transition: 'transform 0.2s ease',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            {t('btn_recharge')}
          </button>
        </div>
      )}

      <form className="form-card" onSubmit={handleSubmit} noValidate>
        <label className="form-label" htmlFor="video-url-input">
          {t('input_label')}
        </label>

        <div className={`input-wrap ${urlError ? 'input-wrap--error' : ''}`}>
          <span className="input-icon">🔗</span>
          <input
            id="video-url-input"
            type="url"
            className="url-input"
            value={url}
            onChange={handleUrlChange}
            placeholder={t('input_placeholder')}
            disabled={isLoading}
            autoComplete="off"
            aria-describedby={urlError ? 'url-error-msg' : undefined}
          />
          {/* 클립보드 버튼 */}
          {clipboardBtnVisible && !url && (
            <button
              type="button"
              className="paste-btn"
              onClick={handleClipboardPaste}
              disabled={isLoading}
              id="paste-clipboard-btn"
              aria-label={t('aria_paste')}
            >
              📋
            </button>
          )}
          
          {/* 지우기(X) 버튼 */}
          {url && (
            <button
              type="button"
              className="clear-btn"
              onClick={handleClear}
              disabled={isLoading}
              aria-label={t('aria_clear')}
            >
              ✕
            </button>
          )}
        </div>

        {/* URL 에러 메시지 */}
        {urlError && (
          <p id="url-error-msg" className="url-error" role="alert">
            {urlError}
          </p>
        )}

        {/* 제출 버튼 */}
        <button
          type="submit"
          className="submit-btn"
          disabled={isLoading || !url.trim()}
          id="submit-download-btn"
        >
          {isLoading ? (
            <span className="btn-loading">
              <span className="btn-dot" /><span className="btn-dot" /><span className="btn-dot" />
            </span>
          ) : (
            t('btn_download')
          )}
        </button>

        {/* 지원 플랫폼 태그 */}
        <div className="platform-tags" aria-label="지원 플랫폼">
          {['TikTok', 'Instagram', 'YouTube', 'Reels', 'Twitter'].map(p => (
            <span key={p} className="platform-tag">{p}</span>
          ))}
        </div>
      </form>
    </section>
  );
}

export default DownloadForm;
